import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Add,
  TrendingUp,
  AccessTime,
  Person,
  Receipt,
  Warning,
  Schedule,
} from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { PageHeader, StatCard } from '../components/ui';
import { DashboardSkeleton } from '../components/Skeletons';
import { useDashboard } from '../hooks/useApi';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useTour, hasUserSeenTour } from '../contexts/TourContext';
import tourSteps from '../data/tourSteps';

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data, isLoading, error, refetch } = useDashboard();
  const {
    isComplete: isProfileComplete,
    missingFields,
    completionProgress,
  } = useProfileCompletion();
  const { startTour } = useTour();
  const [chartType, setChartType] = useState<'payments' | 'hours'>('payments');

  // Auto-start tour for new users
  React.useEffect(() => {
    if (!hasUserSeenTour() && data && !isLoading) {
      // Small delay to let the page render
      setTimeout(() => {
        startTour(tourSteps.quickStart);
      }, 1000);
    }
  }, [data, isLoading, startTour]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const currentYear = new Date().getFullYear();

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {(error as any)?.message ||
            'Failed to load dashboard data. Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <div data-tour="dashboard-header">
        <PageHeader
          title="Dashboard"
          subtitle={`Overview of your payroll activity for ${currentYear}`}
          icon={<DashboardIcon />}
          iconColor="primary"
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => startTour(tourSteps.workflow)}
                sx={{ mr: 1 }}
              >
                Take Tour
              </Button>
              <Button
                data-tour="add-payee-btn"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => navigate('/payees')}
              >
                Add Payee
              </Button>
              <Button
                data-tour="new-timesheet-btn"
                variant="contained"
                startIcon={<Schedule />}
                onClick={() => navigate('/timesheet')}
              >
                New Timesheet
              </Button>
            </Box>
          }
        />
      </div>

      {/* Profile Completion Alert */}
      {!isProfileComplete && (
        <Alert
          data-tour="profile-alert"
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/profile')}
              sx={{ fontWeight: 600 }}
            >
              Complete Profile
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Profile Setup Required ({completionProgress}% complete)
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
            Please complete your business profile to start using payroll
            features. Missing: {missingFields.slice(0, 3).join(', ')}
            {missingFields.length > 3 &&
              ` and ${missingFields.length - 3} more`}
          </Typography>
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }} data-tour="dashboard-stats">
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="YTD Payments"
            value={formatCurrency(data?.totalYtdPayments || 0)}
            subtitle={`${currentYear} total`}
            icon={<AttachMoneyIcon sx={{ color: 'white' }} />}
            iconBgColor="linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Contractors"
            value={data?.totalContractors || 0}
            subtitle={`${data?.activeContractors || 0} active`}
            icon={<PeopleIcon sx={{ color: 'white' }} />}
            iconBgColor="linear-gradient(135deg, #2196F3 0%, #1565C0 100%)"
            onClick={() => navigate('/payees')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hours This Month"
            value={(data?.totalHoursThisMonth || 0).toFixed(1)}
            subtitle="Total tracked"
            icon={<AccessTime sx={{ color: 'white' }} />}
            iconBgColor="linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="1099 Ready"
            value={data?.contractorsOver1099Threshold || 0}
            subtitle={`${
              data?.contractorsNear1099Threshold || 0
            } approaching threshold`}
            icon={<DescriptionIcon sx={{ color: 'white' }} />}
            iconBgColor="linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)"
            onClick={() => navigate('/taxes')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* 1099 Threshold Alert */}
        {(data?.contractorsNear1099Threshold || 0) > 0 && (
          <Grid item xs={12}>
            <Card
              sx={{
                bgcolor: 'warning.light',
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Warning color="warning" />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {data?.contractorsNear1099Threshold} contractor(s)
                    approaching $600 threshold
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => navigate('/taxes')}
                    sx={{ ml: 'auto' }}
                  >
                    View Details
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Payment Trends Chart */}
        {data?.monthlyPayments && data.monthlyPayments.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {currentYear} Payment Trends
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={(_, value) => value && setChartType(value)}
                    size="small"
                  >
                    <ToggleButton value="payments">
                      <AttachMoneyIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      Payments
                    </ToggleButton>
                    <ToggleButton value="hours">
                      <AccessTime sx={{ mr: 0.5, fontSize: 18 }} />
                      Hours
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    {chartType === 'payments' ? (
                      <AreaChart
                        data={data.monthlyPayments}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient
                            id="colorPayments"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#1E23FE"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="95%"
                              stopColor="#1E23FE"
                              stopOpacity={0.1}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                        />
                        <YAxis
                          tickFormatter={(value) =>
                            `$${(value / 1000).toFixed(0)}k`
                          }
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          formatter={(value) => [
                            formatCurrency(Number(value) || 0),
                            'Payments',
                          ]}
                          labelStyle={{ fontWeight: 600 }}
                          contentStyle={{
                            borderRadius: 8,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="amount"
                          stroke="#1E23FE"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorPayments)"
                        />
                      </AreaChart>
                    ) : (
                      <BarChart
                        data={data.monthlyPayments}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={theme.palette.divider}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                        />
                        <YAxis
                          tickFormatter={(value) => `${value}h`}
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `${Number(value).toFixed(1)} hours`,
                            'Hours Worked',
                          ]}
                          labelStyle={{ fontWeight: 600 }}
                          contentStyle={{
                            borderRadius: 8,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                          }}
                        />
                        <Bar
                          dataKey="hours"
                          fill="#2196F3"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Activity
                </Typography>
                <Chip
                  label={`${data?.recentTimesheets || 0} timesheets`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />

              {!data?.recentActivity || data.recentActivity.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Receipt
                    sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    No recent activity
                  </Typography>
                  <Button
                    variant="text"
                    startIcon={<Add />}
                    onClick={() => navigate('/timesheet')}
                    sx={{ mt: 1 }}
                  >
                    Submit a timesheet
                  </Button>
                </Box>
              ) : (
                <List disablePadding>
                  {data.recentActivity
                    .slice(0, 5)
                    .map((activity: any, index: number) => (
                      <ListItem
                        key={index}
                        sx={{
                          px: 0,
                          py: 1.5,
                          borderBottom: index < 4 ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor:
                                activity.type === 'timesheet'
                                  ? 'primary.main'
                                  : 'success.main',
                            }}
                          >
                            {activity.type === 'timesheet' ? (
                              <Schedule sx={{ fontSize: 18 }} />
                            ) : (
                              <AttachMoneyIcon sx={{ fontSize: 18 }} />
                            )}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={activity.contractorName}
                          secondary={activity.description}
                          primaryTypographyProps={{ fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'body2' }}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          {activity.amount && (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency(activity.amount)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {format(parseISO(activity.date), 'MMM d')}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Contractors */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top Contractors (YTD)
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {!data?.topContractors || data.topContractors.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Person
                    sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    No contractors yet
                  </Typography>
                  <Button
                    variant="text"
                    startIcon={<Add />}
                    onClick={() => navigate('/payees')}
                    sx={{ mt: 1 }}
                  >
                    Add your first payee
                  </Button>
                </Box>
              ) : (
                <List disablePadding>
                  {data.topContractors.map((contractor: any, index: number) => (
                    <ListItem
                      key={contractor.id}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderBottom:
                          index < data.topContractors.length - 1
                            ? '1px solid'
                            : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: `hsl(${(index * 60) % 360}, 70%, 50%)`,
                            fontSize: 14,
                          }}
                        >
                          {contractor.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {contractor.name}
                            {contractor.isNear1099Threshold && (
                              <Chip
                                label="Near $600"
                                size="small"
                                color="warning"
                                sx={{ height: 20, fontSize: 10 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={`${contractor.hoursThisMonth.toFixed(
                          1
                        )} hrs this month`}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCurrency(contractor.ytdPayments)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => navigate('/payees')}
                >
                  Add New Payee
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Schedule />}
                  onClick={() => navigate('/timesheet')}
                >
                  Submit Timesheet
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Receipt />}
                  onClick={() => navigate('/taxes')}
                >
                  View Tax Reports
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TrendingUp />}
                  onClick={() => navigate('/profile')}
                >
                  Business Settings
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
